import {getUrlId, HOME_ROUTES, ROUTES, stringToHslColor, SUPER_ADMIN_PANEL_ROUTES} from "@source/utils/utils";
import {useEffect, useState} from "react";
import {NavLink, useHistory, useLocation, useParams} from "react-router-dom";
import Button from "../UI/buttons/button/button";
import styles from "./appHeader.module.scss";
import iconHouseFilled from "@source/assets/icons/iconHouseFilled.svg";
import iconLogout from "@source/assets/icons/iconLogout.svg";
import openMenuForward from '@source/assets/icons/openMenuForward.svg';
import Dropdown, {IDropdownItem} from "../UI/dropdown/dropdown";
import {observer} from "mobx-react-lite";
import Svg from "../UI/svg/svg";
import AuthController from "@source/api/controllers/authController";
import residentsStore from "@source/store/residentsStore";
import updatesStore from "@source/store/updatesStore";
import userStore from "@source/store/userStore";
import nursingHomeStore from "@source/store/nursingHomeStore";
import {INursingHome, SELECTED_NURSING_HOME_ID} from "@source/api/models/nursingHomeModels";
import appStore from "@source/store/appStore";
import {HomePermissionsEnum, OrganizationPermissionsEnum} from "@source/api/models/permissionsModels";
import moment from "moment";
import CacheImg from "@components/UI/cacheImg/cacheImg";
import ConfirmModal from "@components/confirmModal/confirmModal";
import iconConfirmed from '@source/assets/icons/iconConfirmed.svg';
import {ProgressBar} from "react-rainbow-components";
import SuccessModal from "@components/successModal/successModal";
import useFetching from "@source/hooks/useFetching";
import {Role} from "@source/utils/models/adminModels";
import adminAgencyStore from '@source/store/adminAgencyStore';
import {useFirebaseLogEvent} from "@source/utils/hooks/useFirebaseLogEvent";
import announcementsStore from "@store/announcementsStore";
import StepBackBtn from "@pages/HomePage/homeRouter/updateTab/steps/step/stepBackBtn/stepBackBtn";
import adminStore from "@store/adminStore";
import {
  canManageConsentTemplates,
  canManageConsentDocuments,
  canViewConsentCategories,
  canViewConsentDocuments,
  canViewConsentTemplates,
  canDownloadConsentReports,
  isConsentModuleEnabled,
} from "@source/utils/consentAccess";

const AppHeader = observer((props: AppHeaderProps) => {
  const {} = props;

  const [resendConfirmationEmail, resendConfirmationEmailLoading, resendConfirmationEmailError] = useFetching<ReturnType<typeof AuthController.resendConfirmationEmail>>(AuthController.resendConfirmationEmail);

  const [dropdownItems, setDropdownItems] = useState<IDropdownItem[]>([]);
  
  const router = useHistory();
  const location = useLocation();
  const params = useParams<{residentId?: string, updateId?: string}>();
  const searchParams = new URLSearchParams(location.search);
  const residentPathMatch = location.pathname.match(
    new RegExp(`^${HOME_ROUTES.Residents}/([^/]+)(/consent-documents)?/?$`),
  );
  const residentIdFromPath = residentPathMatch?.[1];
  const isResidentConsentHistory = Boolean(residentPathMatch?.[2]);
  const isResidentOverview = Boolean(residentIdFromPath) && !isResidentConsentHistory;
  const showResidentActions = isResidentOverview || isResidentConsentHistory;

  const [logEvent] = useFirebaseLogEvent();

  useEffect(() => {
    const newDropdownItems = nursingHomeStore.nursingHomes.map((nursingHome) => {
      return {
        onClick: () => onNursingHomeClick(nursingHome),
        text: nursingHome.name,
      }
    })

    setDropdownItems(newDropdownItems);
  }, [nursingHomeStore.nursingHomes])

  const onNewResident = () => {
    router.push(HOME_ROUTES.NewResident);
  }

  const onBackResidents = () => {
    router.push(`${HOME_ROUTES.Residents}`);
    residentsStore.setFocusOnResident(residentsStore.getActiveResident());
  }

  const onNewUpdate = () => {
    if (!residentsStore.getActiveResidentUpdateEnabled() || residentsStore.getActiveResidentFamilyMemberChoseNotToUseApp()) {
      residentsStore.setModalNewUpdatesDisabledResident(residentsStore.getActiveResident());
      return;
    }

    if (!residentsStore.isActiveResidentPrimaryContactActivated()) {
      residentsStore.setModalBlockNewUpdatesResident(residentsStore.getActiveResident());
      return;
    }

    if (residentsStore.isActiveResidentPrimaryContactActivated()) {
      logEvent('updates__click_new_update_button', {
        home: nursingHomeStore.getActiveNursingHome()?.name || "",
        organization: nursingHomeStore.getActiveNursingHome()?.organization?.name || "",
      });
      router.push(`${HOME_ROUTES.Residents}/${residentsStore.activeResident?._id}/update`);
    }
  }

  const onNursingHomeClick = (nursingHome: INursingHome) => {
    logEvent('change_home', {
      home: nursingHome.name,
      organization: nursingHome.organization.name,
    })

    nursingHomeStore.setActiveNursingHome(nursingHome);
    nursingHomeStore.setLocalStorageActiveNursingHome(nursingHome._id);
  }

  const getResidentCountPercents = () => {
    const activeCount = residentsStore.getResidentsCount()?.activeCount;
    const totalCount = residentsStore.getResidentsCount()?.totalCountWithPrimary;

    return activeCount && totalCount ? Math.round((activeCount / totalCount) * 100) : 0
  }
  
  const onResendEmail = async () => {
    const resendConfirmationEmailRes = await resendConfirmationEmail(residentsStore.editFamilyMember?.familyMember.email);

    if (resendConfirmationEmailRes) {
      logEvent('admin__family_members__resend_invitation', {
        home: nursingHomeStore.getActiveNursingHome()?.name || "",
        organization: nursingHomeStore.getActiveNursingHome()?.organization.name || "",
      })
    }

    return !!resendConfirmationEmailRes
  }

  const residentHeaderActions = (
    <div className={`${styles.buttons} ${showResidentActions ? styles.residentActions : ""}`}>
      <Button onClick={onBackResidents} size="small" variant="additional" className={styles.backBtnList}>
        Back to list of residents
      </Button>
      {showResidentActions && isConsentModuleEnabled() && canManageConsentDocuments() && residentIdFromPath ?
        <Button
          className={styles.newConsentBtn}
          event="link"
          link={`${HOME_ROUTES.Residents}/${residentIdFromPath}/consent-documents/new`}
          size="small"
        >
          New Form
        </Button> :
        null}
      {userStore.isPermission(HomePermissionsEnum.ADD_UPDATE) ?
        <Button className={styles.newUpdateBtn} onClick={onNewUpdate} size="small" event="submit">
          New Update
        </Button> :
        null}
    </div>
  );

  const getResendInvitaionItems = (invitations: string[]) => {
    return invitations.map((invitation, index) => {
      return {
        onClick: () => {},
        text: (<span>{index === 0 ? "Initial" : "Reminder"}: <span style={{fontWeight: '600'}}>{moment(invitation).format("DD MMM YYYY")} at {moment(invitation).format("hh:mm A")}</span></span>),
        style: {
          pointerEvents: 'none',
        },
      } 
    })
  }

  const getHeader = () => {
    if (
      new RegExp(`^${HOME_ROUTES.Residents}/[^/]+/consent-documents(?:/new|/[^/]+/(?:edit|presentation|clinical-presentation|family-presentation))$`).test(location.pathname) &&
      isConsentModuleEnabled()
    ) {
      const consentHeader = appStore.consentResidentHeader;
      if (!consentHeader) return null;
      const resident = consentHeader?.resident;
      const residentName = `${resident?.firstName || ""} ${resident?.lastName || ""}`.trim() || "Resident";
      const initials = `${resident?.firstName?.[0] || ""}${resident?.lastName?.[0] || ""}`.toUpperCase() || "R";
      return <div className={consentHeader.usePreviousStepNavigation ? styles.update : `${styles.update} ${styles.residentProcessHeader}`}>
        <CacheImg url={resident?.photo} alt={`${residentName} avatar`} className={styles.photo} imgClassName={styles.photo} skeletonProps={{variant: "circular"}}>
          <div className={styles.noPhoto} style={{backgroundColor: stringToHslColor(residentName)}}>{initials}</div>
        </CacheImg>
        <div>
          <div className={styles.consentResidentTitle}>{consentHeader.title}</div>
          <div className={styles.updateSubtitle}>{consentHeader.subtitle}</div>
        </div>
        <div className={styles.backBtn}>
          {consentHeader.usePreviousStepNavigation
            ? <StepBackBtn onClick={consentHeader.onBack || consentHeader.onCancel}/>
            : <>{consentHeader.onBack ? <Button size="small" variant="additional" onClick={consentHeader.onBack}>Back</Button> : null}
              {consentHeader.onPresentLater ? <Button size="small" variant="additional" onClick={consentHeader.onPresentLater}>Present later</Button> : null}
              <Button size="small" variant="additional" onClick={consentHeader.onCancel}>Cancel</Button></>}
        </div>
      </div>;
    }
    if (location.pathname.startsWith(HOME_ROUTES.ConsentDocuments) && isConsentModuleEnabled()) {
      const isTopLevelConsentRoute = [
        HOME_ROUTES.ConsentDocumentsHistory,
        HOME_ROUTES.ConsentDocumentsAwaitingYourSign,
        HOME_ROUTES.ConsentDocumentsTemplates,
        HOME_ROUTES.ConsentDocumentsCategories,
      ].includes(location.pathname);
      const consentTitle = getConsentHeaderTitle();
      return (
        <div className={styles.adminHeader}>
          <div className={styles.header}>
            <span className={styles.title}>{consentTitle}</span>
            {location.pathname === HOME_ROUTES.ConsentDocumentsHistory || location.pathname === HOME_ROUTES.ConsentDocumentsAwaitingYourSign ?
              getHomeDropdown(styles.gMenu) :
              null}
            {location.pathname === HOME_ROUTES.ConsentDocumentsTemplates && canManageConsentTemplates() ?
              <Button className={styles.newConsentBtn} event="link" link={HOME_ROUTES.ConsentDocumentsNewTemplate} size="small">
                New template
              </Button> :
              null}
            {location.pathname === HOME_ROUTES.ConsentDocumentsCategories &&
              userStore.isPermission(OrganizationPermissionsEnum.MANAGE_CONSENT_CATEGORIES) ?
              <Button className={styles.newConsentBtn} event="link" link={HOME_ROUTES.ConsentDocumentsNewCategory} size="small">
                New Form Category
              </Button> :
              null}
            {location.pathname.startsWith(`${HOME_ROUTES.ConsentDocuments}/documents/`) ?
              <div className={`${styles.buttons} ${styles.residentActions}`}>
                <Button size="small" variant="additional" className={styles.backBtnList} onClick={() => {
                  const returnTo = (location.state as {returnTo?: string} | null)?.returnTo;
                  router.push(returnTo || HOME_ROUTES.ConsentDocumentsHistory);
                }}>Back to history</Button>
              </div> :
              null}
          </div>
          {isTopLevelConsentRoute ?
            <div className={styles.nav}>
              {canViewConsentDocuments() ?
                <NavLink exact to={HOME_ROUTES.ConsentDocumentsHistory} className={styles.nav__item} activeClassName={styles.nav__item_active}>
                  History
                </NavLink> :
                null}
              {userStore.getId() ?
                <NavLink exact to={HOME_ROUTES.ConsentDocumentsAwaitingYourSign} className={styles.nav__item} activeClassName={styles.nav__item_active}>
                  Awaiting Your Signature
                </NavLink> :
                null}
              {canViewConsentTemplates() ?
                <NavLink exact to={HOME_ROUTES.ConsentDocumentsTemplates} className={styles.nav__item} activeClassName={styles.nav__item_active}>
                  Templates
                </NavLink> :
                null}
              {canViewConsentCategories() ?
                <NavLink exact to={HOME_ROUTES.ConsentDocumentsCategories} className={styles.nav__item} activeClassName={styles.nav__item_active}>
                  Form Categories
                </NavLink> :
                null}
            </div> :
            null}
        </div>
      );
    }

    if(location.pathname.includes(HOME_ROUTES.ResidentsArchive)) {
      return (
        <>
          <span className={styles.title}>
            {
              residentsStore.getActiveArchiveResident() ?
              "Resident"
              :
              "Residents archive"
            }
          </span>

          {
            residentsStore.getActiveArchiveResident() ?
              <div className={styles.buttons}>
                <span className={styles.acrchive}>Archived {residentsStore.getActiveArchiveResident()?.archivedDate && moment(residentsStore.getActiveArchiveResident()?.archivedDate).format("DD MMM YYYY")}</span>

                <Button  onClick={onBackResidentsArchive} size="small"  
                  variant="additional" className={styles.backBtnList}
                  >Back to acrhive
                </Button>
              </div>
              :
              getHomeDropdown()
          }
        </>
      )
    }

    if(location.pathname.includes(HOME_ROUTES.Residents)) {
      if(location.pathname.includes(HOME_ROUTES.NewResident)) {
        if (residentsStore.editResident) {
          return (
            <>
              <span className={styles.title}>Edit resident</span>
            </>
          )
        }

        return (
          <>
            <span className={styles.title}>Add resident</span>
          </>
        )
      }

      if(residentsStore.editFamilyMember) {
        return (
          <>
            <span className={styles.title}>Edit family member</span>

            {
              residentsStore.editFamilyMember?.familyMember?.account?.isTemporaryPassword && userStore.isPermission(OrganizationPermissionsEnum.RESEND_FAMILY_INVITATION)?
                <div className={styles.resendInvitation}>
                  <SuccessModal
                      cb={() => onResendEmail()}
                      text="Account activation reminder was sent successfully."
                    >
                      <Button
                        className={styles.confirmationAccountBtn}
                        event="visual"
                        isLoading={resendConfirmationEmailLoading}
                      >
                        Resend Invitation
                      </Button>
                    </SuccessModal>

                    {
                      residentsStore.editFamilyMember.familyMember.invitations.length > 0 ?
                        <div className={styles.invitations}>
                          <Dropdown className={styles.dropdown} items={getResendInvitaionItems(residentsStore.editFamilyMember.familyMember.invitations)} arrow>
                              <span className={styles.dropdown__text}>Click to show email history</span>
                          </Dropdown>
                        </div>
                        :
                        <></>
                    }
                </div>
              :
              <></>
            }
          </>
        )
      }

      if (residentsStore.addResidentFamilyMembers) {
        return (
          <>
            <span className={styles.title}>Add family member</span>
          </>
        )
      }

      
      if(location.pathname.includes('update')) {
        const residentId = getUrlId(location.pathname, 'residents');

        if(!residentsStore.getResidents()?.length) {
          router.push(HOME_ROUTES.Residents);
          return;
        }

        const resident = residentsStore.getResidents()?.find((resident) => resident._id === residentId);
        const multipleUpdateSelectedResidents = residentsStore.getMultipleUpdateSelectedResidents();
        return (
          <div className={styles.update}>
            {
              multipleUpdateSelectedResidents ?
                <>
                  {
                    multipleUpdateSelectedResidents.length > 3 ?
                      (
                        <>
                          {
                            multipleUpdateSelectedResidents.slice(0, 2).map((selectedResident) => (
                              <CacheImg 
                                url={selectedResident?.photo} 
                                alt="avatar" 
                                className={`${styles.photo} ${styles.photo_multiple}`} 
                                imgClassName={`${styles.photo} ${styles.photo_border}`} 
                                skeletonProps={{variant: 'circular'}}
                              >
                                <div className={styles.noPhoto} style={{backgroundColor: selectedResident ? stringToHslColor(selectedResident.firstName + selectedResident.lastName) : ""}}>
                                  {selectedResident?.firstName[0] + " " + selectedResident?.lastName[0]}
                                </div>
                              </CacheImg>
                            ))
                          }

                          <CacheImg 
                            url={""} 
                            alt="avatar" 
                            className={`${styles.photo} ${styles.photo_multiple}`} 
                            imgClassName={`${styles.photo} ${styles.photo_border}`} 
                            skeletonProps={{variant: 'circular'}}
                          >
                            <div className={styles.noPhoto} style={{backgroundColor: "hsl(0.62, 100, 99)"}}>
                              +{multipleUpdateSelectedResidents.length - 3}
                            </div>
                          </CacheImg>
                        </>
                      )
                      : 
                      (
                        multipleUpdateSelectedResidents.map((selectedResident) => (
                          <CacheImg 
                            url={selectedResident?.photo} 
                            alt="avatar" 
                            className={`${styles.photo} ${styles.photo_multiple}`} 
                            imgClassName={`${styles.photo} ${styles.photo_border}`} 
                            skeletonProps={{variant: 'circular'}}
                          >
                            <div className={styles.noPhoto} style={{backgroundColor: selectedResident ? stringToHslColor(selectedResident.firstName + selectedResident.lastName) : ""}}>
                              {selectedResident?.firstName[0] + " " + selectedResident?.lastName[0]}
                            </div>
                          </CacheImg>
                        )
                    ))
                  }
      
                  Multiple Resident Update
                </>
                :
                <>
                  <CacheImg 
                    url={resident?.photo} 
                    alt="avatar" 
                    className={styles.photo} 
                    imgClassName={styles.photo} 
                    skeletonProps={{variant: 'circular'}}
                  >
                    <div className={styles.noPhoto} style={{backgroundColor: resident ? stringToHslColor(resident.firstName + resident.lastName) : ""}}>
                      {resident?.firstName[0] + " " + resident?.lastName[0]}
                    </div>
                  </CacheImg>
      
                  {resident?.firstName + ' ' + resident?.lastName}
                </>
            }

            {!!appStore.getUpdateHeaderBtn()
              ? <div className={styles.backBtn}>
                  {appStore.getUpdateHeaderBtn()}
                </div>
              : <></>}
          </div>
        )
      }
    }

    if(location.pathname.includes(HOME_ROUTES.Residents)) {
      if (appStore.getIsMobile() && (residentsStore.getActiveResident() || showResidentActions)) {
        return residentHeaderActions;
      }
      return (
        <>
          {
            residentsStore.activeResident || showResidentActions ?
              <span className={styles.title}>
                Resident
              </span>
              :
              <div className={`${styles.blockTitle} ${styles.blockTitle_residents}`}>
                <span className={styles.title}>
                  Residents

                  {
                    userStore.isPermission(HomePermissionsEnum.ADD_NEW_RESIDENT) ?
                      <div className={styles.adminIcon} onClick={onNewResident}>+</div>
                      :
                      ""
                  }
                </span>
                
                {
                  residentsStore.getResidentsCount() ?
                      <div className={styles.residentsCount}>
                        <div className={styles.info}>
                        <span className={styles.text}>Active: <span
                            className={styles.bold}>{residentsStore.getResidentsCount()?.activeCount}</span> out of <span
                            className={styles.bold}>{residentsStore.getResidentsCount()?.totalCountWithPrimary}</span> (with primary)</span>

                          <div className={styles.percents}>
                            <span className={styles.text}>{getResidentCountPercents()}%</span>

                            <img className={styles.confirmedIcon} src={iconConfirmed} alt="confirmed"/>
                          </div>
                        </div>

                        <div className={styles.progressBar}>
                          <ProgressBar className={styles.bar} value={getResidentCountPercents()} size="medium"/>
                        </div>
                        <div className={styles.info}>
                        <span className={styles.text}>Residents without primary contact: <span
                            className={styles.bold}>{(residentsStore.getResidentsCount()?.totalCount || 0) - (residentsStore.getResidentsCount()?.totalCountWithPrimary || 0)}</span></span>
                          <span className={styles.text}>Total residents: <span
                              className={styles.bold}>{(residentsStore.getResidentsCount()?.totalCount || 0)}</span></span>
                        </div>
                      </div>
                      :
                      <div className={styles.residentsCount}/>
                }
              </div>
          }

          {
            residentsStore.getActiveResident() || showResidentActions ?
                residentHeaderActions
              :
              getHomeDropdown()
          }
        </>
      )
    }

    if(location.pathname.includes(HOME_ROUTES.Chat)) {
      return <span className={styles.title}>Chat with familys</span>
    }

    if(location.pathname.includes(HOME_ROUTES.History)) {
      return (
        <>
        <div className={`${styles.blockTitle} ${styles.blockTitle_residents}`}>
          <span className={styles.title}>Updates history</span>
          {
            updatesStore.getTotalSent() ?
                <div className={styles.residentsCount} style={{ marginLeft: '2px', marginTop: '0px'}}>
                  <div className={styles.info}>
                    <span className={styles.text}>Total updates sent: <span className={styles.bold}>{updatesStore.getTotalSent()}</span></span>
                  </div>
                </div>
                :
                <div className={styles.residentsCount}/>
          }
        </div>
          {getHomeDropdown()}
        </>
      )
    }

    if(location.pathname.includes(HOME_ROUTES.Chat)) {
      return <span className={styles.title}>Chat with familys</span>
    }

    if(location.pathname.includes(HOME_ROUTES.Settings)) {
      return (
        <div className={styles.settings_container}>
          <div className={styles.settings}>
            <span className={styles.title}>Settings</span>

            <span className={styles.loginDate}>
              Last login date: {userStore.getUserInfo()?.loginDate ? moment(userStore.getUserInfo()?.loginDate).format("DD.MM.YYYY hh:mm A") : ""}
            </span>
          </div>

          <div className={styles.settingsRight}>
            {
              userStore.getUserInfo()?.account.role === Role.SUPER_ADMIN ?
                <ConfirmModal
                  confirmCb={() => onChangeOrganization()}
                  className={styles.changeOrganization}
                  title="Are you sure you want to change the organisation?"
                >
                  <Button   
                    className={styles.changeOrganizationBtn}
                    size="small"
                    event="submit"
                  >
                    Change organisation
                  </Button>
                </ConfirmModal>
                :
                <></>
            }
            <ConfirmModal
              confirmCb={() => onLogout()}
              className={styles.logout}
              title="Are you sure you want to logout?"
            >
              <Svg
                  className={styles.icon}
                  src={iconLogout}
                  width={24}
                  height={24}
                  color="#E7313C"
              />

              <span className={styles.text}>Logout</span>
            </ConfirmModal>
          </div>
        </div>
      )
    }

    if(location.pathname.includes(HOME_ROUTES.NewAnnouncement)) {
      return (
        <span className={styles.title}>New announcement</span>
      )
    }

    if (location.pathname.includes(HOME_ROUTES.Information)) {
      if(appStore.getIsMobile()) {
        return <div className={styles.adminHeader}>
          <div className={styles.nav}>
            {
              userStore.isPermission(HomePermissionsEnum.DOWNLOAD_UNREAD_UPDATES) ||
              userStore.isPermission(HomePermissionsEnum.VIEW_HOME_STATISTIC) ||
              userStore.isPermission(HomePermissionsEnum.DOWNLOAD_UPDATE_HISTORY) ||
              canDownloadConsentReports()
                  ?
                  <NavLink
                      to={HOME_ROUTES.InformationExports}
                      className={styles.nav__item}
                      activeClassName={styles.nav__item_active}
                  >
                    Reports
                  </NavLink>
                  :
                  <></>
            }
            {
              userStore.isPermission(HomePermissionsEnum.VIEW_HOME_STATISTIC)
                  ?
                  <NavLink
                      to={HOME_ROUTES.InformationStats}
                      className={styles.nav__item_statistics}
                      activeClassName={styles.nav__item_statistics_active}
                  >
                    Statistics
                  </NavLink>
                  :
                  <></>
            }
          </div>
        </div>
      }
      return <div className={styles.adminHeader}>
        <div className={styles.header}>
            <span className={styles.title}>
              Information
            </span>

          <div className={styles.gMenu}>
            <img className={styles.icon} src={iconHouseFilled} alt="home"/>

            <div className={styles.select}>
              <Dropdown className={styles.dropdown} items={dropdownItems} arrow>
                <span className={styles.dropdown__text}>{nursingHomeStore.activeNursingHome?.name}</span>
              </Dropdown>
            </div>
          </div>
        </div>

        <div className={styles.nav}>
          {
            userStore.isPermission(HomePermissionsEnum.DOWNLOAD_UNREAD_UPDATES) ||
            userStore.isPermission(HomePermissionsEnum.VIEW_HOME_STATISTIC) ||
            userStore.isPermission(HomePermissionsEnum.DOWNLOAD_UPDATE_HISTORY) ||
            canDownloadConsentReports()
                ?
                <NavLink
                    to={HOME_ROUTES.InformationExports}
                    className={styles.nav__item}
                    activeClassName={styles.nav__item_active}
                >
                  Reports
                </NavLink>
                :
                <></>
          }
          {
            userStore.isPermission(HomePermissionsEnum.VIEW_HOME_STATISTIC)
                ?
                <NavLink
                    to={HOME_ROUTES.InformationStats}
                    className={styles.nav__item}
                    activeClassName={styles.nav__item_active}
                >
                  Statistics
                </NavLink>
                :
                <></>
          }
        </div>
      </div>
    }
    if (location.pathname.includes(HOME_ROUTES.Announcements)) {
      return (
          <div className={styles.announcementHeader}>
            <span className={styles.title}>Announcements</span>

            <div className={styles.buttons}>
              {
                userStore.isPermission(HomePermissionsEnum.ADD_NEW_ANNOUNCEMENT_EVENT) ?
                    <Button
                        className={styles.newAnnouncementBtn}
                        onClick={onNewAnnouncement}
                        size="small"
                        event="submit"
                    >
                      New
                    </Button>
                    :
                    <></>
              }
            </div>
          </div>
      )
    }

    if (location.pathname.includes(HOME_ROUTES.NewHome)) {
      return (
          <>
          <span className={styles.title}>
            {
              nursingHomeStore.editedNursingHome ?
                  "Edit nursing home"
                  :
                  "New nursing home"
            }
          </span>
          </>
      )
    }

    if (location.pathname.includes(HOME_ROUTES.NewEmployee)) {
      return (
          <span className={styles.title}>
            New employee
          </span>
      )
    }

    if (location.pathname.includes(HOME_ROUTES.NewAgencyAccount)) {
      return (
          <span className={styles.title}>
          Add new agency nurse account
        </span>
      )
    }

    if (location.pathname.includes(HOME_ROUTES.AgencyAccountChangePassword)) {
      return (
          <span className={styles.title}>
          Change password for&nbsp;<span
              className={styles.blueBold}>{adminAgencyStore.getEditAgencyAccountPassword()?.email}</span>
        </span>
      )
    }

    if (location.pathname.includes(HOME_ROUTES.EmployeeChangePassword)) {
      return (
          <span className={styles.title}>
          Change password for {adminStore.getEditEmployee()?.firstName} {adminStore.getEditEmployee()?.lastName}
        </span>
      )
    }

    if (location.pathname.includes(HOME_ROUTES.AgencyAccountForgotPassword)) {
      return (
          <div className={styles.agencyForgotPassword}>
          <span className={styles.title}>
            Forgot password
          </span>

            <span className={styles.subTitle}>
            We will send a confirmation link to reset your password
          </span>
          </div>
      )
    }

    if (location.pathname.includes(HOME_ROUTES.AgencyAccountForgotPassword)) {
      return (
          <div className={styles.agencyForgotPassword}>
          <span className={styles.title}>
            Forgot password
          </span>

            <span className={styles.subTitle}>
            We will send a confirmation link to reset your password
          </span>
          </div>
      )
    }

    if (location.pathname.includes(HOME_ROUTES.AgencyAccountSetPassword)) {
      return (
          <div className={styles.agencySetPassword}>
          <span className={styles.title}>
            Forgot password
          </span>

            <span className={styles.subTitle}>
            Please create and confirm new password for&nbsp;<span
                className={styles.bold}>{searchParams.get('email')}</span>
          </span>
          </div>
      )
    }

    if (location.pathname.includes(HOME_ROUTES.AdminPanel)) {
      return (
          <div className={styles.adminHeader}>
            <div className={styles.header}>
              {
                appStore.getIsMobile() ? <></> :
                    <span className={styles.title}>
                      Admin panel
                    </span>
              }

              {
                location.pathname.includes(HOME_ROUTES.AdminPanelStaff) && userStore.isPermission(OrganizationPermissionsEnum.ADD_NEW_EMPLOYEE) && !appStore.getIsMobile()?
                    <Button
                        className={styles.newEmployeeBtn}
                        onClick={onAddNewEmployee}
                        size="small"
                        event="submit"
                    >
                      + Add new employee
                    </Button>
                    :
                    <></>
              }

              {
                location.pathname.includes(HOME_ROUTES.AdminPanelHouses) && userStore.isPermission(OrganizationPermissionsEnum.ADD_NEW_HOUSE) && !appStore.getIsMobile() ?
                    <Button
                        className={styles.newEmployeeBtn}
                        onClick={onAddNewHouse}
                        size="small"
                        event="submit"
                    >
                      + Add new home
                    </Button>
                    :
                    <></>
              }

              {
              location.pathname.includes(HOME_ROUTES.AdminPanelAgencyStaff) && userStore.isPermission(OrganizationPermissionsEnum.REGISTER_ROOT_AGENCY_ACCOUNT) && !appStore.getIsMobile() ?
                <Button 
                  className={styles.newAgencyAccountBtn}
                  onClick={onAddNewAgencyAccount}
                  size="small"
                  event="submit"
                >
                  + Add new agency nurse account
                </Button>
                :
                <></>
            }
          </div>
          
          <div className={styles.nav}>
            {
              userStore.isPermission(OrganizationPermissionsEnum.EDIT_EMPLOYEE_PASSWORD) ||
              // userStore.isPermission(OrganizationPermissionsEnum.EDIT_EMPLOYEE_PERMISSION) ||
              userStore.isPermission(OrganizationPermissionsEnum.ENABLE_EMPLOYEE) ||
              userStore.isPermission(OrganizationPermissionsEnum.ADD_NEW_EMPLOYEE) ||
              userStore.isPermission(OrganizationPermissionsEnum.DISABLE_EMPLOYEE) ||
              userStore.isPermission(OrganizationPermissionsEnum.EDIT_EMPLOYEE_PHOTO) ||
              userStore.isPermission(OrganizationPermissionsEnum.RESEND_EMPLOYEE_INVITATION)
                ?
              <NavLink 
                to={HOME_ROUTES.AdminPanelStaff} 
                className={styles.nav__item} 
                activeClassName={styles.nav__item_active}
              >
                Employees
              </NavLink>
              :
              <></>
            }
            {
              userStore.isPermission(OrganizationPermissionsEnum.DISABLE_FAMILY_MEMBER) ||
              userStore.isPermission(OrganizationPermissionsEnum.ENABLE_FAMILY_MEMBER) ||
              userStore.isPermission(OrganizationPermissionsEnum.MARK_RESIDENTS_AS_NON_PARTICIPANT) ||
              userStore.isPermission(OrganizationPermissionsEnum.RESEND_FAMILY_INVITATION)
               ?
              <NavLink 
                to={HOME_ROUTES.AdminPanelFamilyMembers} 
                className={styles.nav__item} 
                activeClassName={styles.nav__item_active}
              >
                Family members
              </NavLink>
              :
              <></>
            }
            {
              userStore.isPermission(OrganizationPermissionsEnum.ADD_NEW_HOUSE) ||
              userStore.isPermission(OrganizationPermissionsEnum.VIEW_ACCESS_KEYS) ||
              userStore.isPermission(OrganizationPermissionsEnum.CREATE_ACCESS_KEYS) ||
              userStore.isPermission(OrganizationPermissionsEnum.UPDATE_ACCESS_KEYS)
               ?
              <NavLink 
                to={HOME_ROUTES.AdminPanelHouses} 
                className={styles.nav__item} 
                activeClassName={styles.nav__item_active}
              >
                Homes
              </NavLink>
              :
              <></>
            }
            {
              userStore.isPermission(OrganizationPermissionsEnum.VIEW_ROOT_AGENCY_ACCOUNTS)
               ?
              <NavLink 
                to={HOME_ROUTES.AdminPanelAgencyStaff} 
                className={styles.nav__item} 
                activeClassName={styles.nav__item_active}
              >
                Agency staff
              </NavLink>
              :
              <></>
            }
          </div>
        </div>
      )
    }
  }

  const getHomeDropdown = (className = styles.menu) => {
    if (appStore.getIsMobile()) {
      return null;
    }

    return (
      <div className={className}>
        <img className={styles.icon} src={iconHouseFilled} alt="home"/>

        <div className={styles.select}>
          <Dropdown className={styles.dropdown} items={dropdownItems} arrow>
              <span className={styles.dropdown__text}>{nursingHomeStore.activeNursingHome?.name}</span>
          </Dropdown>
        </div>
      </div>
    )
  }

  const getConsentHeaderTitle = () => {
    if (location.pathname === HOME_ROUTES.ConsentDocumentsHistory) return "Form History";
    if (location.pathname === HOME_ROUTES.ConsentDocumentsAwaitingYourSign) return "Awaiting Your Signature";
    if (location.pathname === HOME_ROUTES.ConsentDocumentsTemplates) return "Form Templates";
    if (location.pathname === HOME_ROUTES.ConsentDocumentsCategories) return "Form Categories";
    if (location.pathname === HOME_ROUTES.ConsentDocumentsNewTemplate) return "New template";
    if (location.pathname === HOME_ROUTES.ConsentDocumentsNewBlankTemplate) return "New form template";
    if (location.pathname === HOME_ROUTES.ConsentDocumentsNewCategory) return "New Form Category";
    if (location.pathname.startsWith(`${HOME_ROUTES.ConsentDocumentsTemplates}/`)) {
      return canManageConsentTemplates() ? "Edit form template" : "Form template";
    }
    if (location.pathname.startsWith(`${HOME_ROUTES.ConsentDocumentsCategories}/`)) {
      return userStore.isPermission(OrganizationPermissionsEnum.MANAGE_CONSENT_CATEGORIES) ? "Edit Form Category" : "Form Category";
    }
    if (location.pathname.startsWith(`${HOME_ROUTES.ConsentDocuments}/documents/`)) return "Forms";
    if (location.pathname.startsWith(`${HOME_ROUTES.ConsentDocuments}/sign/`)) return "Review and acknowledgement";

    return "Forms";
  }

  const onNewAnnouncement = () => {
    announcementsStore.setEditAnnouncement(null)
    router.push(HOME_ROUTES.NewAnnouncement);
  }

  const onAddNewEmployee = () => {
    router.push(HOME_ROUTES.NewEmployee);
  }

  const onAddNewHouse = () => {
    router.push(HOME_ROUTES.NewHome);
  }

  const onAddNewAgencyAccount = () => {
    router.push(HOME_ROUTES.NewAgencyAccount);
  }
  
  const onBackResidentsArchive = () => {
    router.push(`${HOME_ROUTES.ResidentsArchive}`)
  }

  const onLogout = () => {
    logEvent("on_logout_button", {
      email: userStore.getUserInfo()?.email || "",
    })

    AuthController.logout();
    const preferredLoginUrl = localStorage.getItem('preferredLoginPage');
    if(preferredLoginUrl) {
      router.push(preferredLoginUrl);
      return;
    }
    router.push(ROUTES.LoginPage)
  }

  const onChangeOrganization = () => {
    userStore.setOrganizationId(null);
    localStorage.removeItem(SELECTED_NURSING_HOME_ID);

    logEvent('organization_admin__change_organization', {});

    router.push(SUPER_ADMIN_PANEL_ROUTES.Organisations);
  }

  const onBackToUpdates = () => {
    router.push(HOME_ROUTES.History);
  }

  const getMobileHeader = () => {
    if (!appStore.getIsMobile()) return null;

    return (
      <div className={styles.mobileHeader}>
        <div className={styles.mobileMenuButton} onClick={() => appStore.toggleMobileSidebar()}>
          <img className={styles.toggle__btn} src={openMenuForward} alt="menu"/>
        </div>

        <div className={styles.mobileHomeDropdown}>
          <img className={styles.icon} src={iconHouseFilled} alt="home"/>
          <div className={styles.select}>
            <Dropdown className={styles.dropdown} items={dropdownItems} arrow>
              <span className={styles.dropdown__text}>{nursingHomeStore.activeNursingHome?.name}</span>
            </Dropdown>
          </div>
        </div>
      </div>
    );
  }

  return(
      appStore.getIsMobile() ?
          <div className={styles.appHeaderMobile}>
              {getMobileHeader()}
              {getHeader()}
          </div> :
          <div className={styles.appHeader}>
            {getHeader()}
          </div>
  )
});

export default AppHeader;

interface AppHeaderProps {
}

